import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/lib/firebase';

const functions = getFunctions(app, 'us-central1');

// --- 🌐 TRADUCTION IA (Claude Haiku) ---

/**
 * Traduit un texte via la Cloud Function `translateText` (Claude Haiku).
 * @param text texte source
 * @param sourceLang langue source ("en" | "fr" | "auto")
 * @param targetLang langue cible ("en" | "fr")
 */
export const translateText = async (data: {
  text: string;
  sourceLang: string;
  targetLang: string;
}): Promise<{ translation: string; sourceLang: string; targetLang: string }> => {
  const func = httpsCallable(functions, 'translateMessage');
  const result = await func(data);
  return result.data as any;
};
export const translateMessage = translateText;

// --- 📞 COMMUNICATIONS & IA ---

export const startTwilioCall = async (phoneNumber: string) => {
  const makeCall = httpsCallable(functions, 'makecall');
  try {
    const result = await makeCall({ to: phoneNumber });
    return result.data;
  } catch (error) {
    console.error("Erreur d'appel:", error);
    throw error;
  }
};

export const processDocument = async (documentData: any) => {
  const analyzeDoc = httpsCallable(functions, 'analyzedocument');
  try {
    const result = await analyzeDoc({ data: documentData });
    return result.data;
  } catch (error) {
    console.error("Erreur d'analyse IA:", error);
    throw error;
  }
};

// --- 📧 NOTIFICATIONS (Cloud Functions) ---

/**
 * Envoie les confirmations de contact (au client + à l'équipe)
 */
export const sendContactConfirmation = async (data: {
  name: string;
  email: string;
  message: string;
  language: 'fr' | 'en';
}) => {
  const func = httpsCallable(functions, 'sendContactConfirmation');
  return (await func(data)).data;
};

/**
 * Envoie les notifications de réservation de créneaux (Booking)
 */
export const sendBookingNotification = async (data: any) => {
  const func = httpsCallable(functions, 'sendBookingNotification');
  return (await func(data)).data;
};

/**
 * Envoie les notifications liées au cycle de vie du CIM
 */
export const sendCimNotification = async (data: any) => {
  const func = httpsCallable(functions, 'sendCimNotification');
  return (await func(data)).data;
};

/**
 * Envoie les notifications de progression de Deal (étapes, mises à jour)
 */
export const sendDealNotification = async (data: any) => {
  const func = httpsCallable(functions, 'sendDealNotification');
  return (await func(data)).data;
};

// --- 💳 PAIEMENTS (STRIPE) ---

/**
 * Crée une session de paiement Stripe
 */
export const createStripeCheckout = async (data: {
  priceId: string;
  customerEmail?: string;
  dealId?: string;
}) => {
  const func = httpsCallable(functions, 'createStripeCheckout');
  return (await func(data)).data;
};

// ... vos fonctions existantes (Twilio, Claude, etc.)

/**
 * Gère les emails liés aux documents (NDA, LOI, CIM) : envoi, rappel, signature
 */
export const sendDocumentEmail = async (data: any) => {
  const func = httpsCallable(functions, 'sendDocumentEmail');
  return (await func(data)).data;
};

/**
 * Alerte l'admin lorsqu'un vendeur demande de l'assistance
 */
export const sendGuidanceNotification = async (data: {
  sellerEmail: string;
  sellerFirstName: string;
  sellerLastName: string;
  sellerPhone?: string;
  language?: "en" | "fr";
}) => {
  const func = httpsCallable(functions, 'sendGuidanceNotification');
  return (await func(data)).data;
};

/**
 * Confirme une réunion à l'organisateur et invite tous les participants
 */
export const sendMeetingConfirmation = async (data: any) => {
  const func = httpsCallable(functions, 'sendMeetingConfirmation');
  return (await func(data)).data;
};

/**
 * Envoie des rappels, annulations ou résumés de réunion
 */
export const sendMeetingEmail = async (data: any) => {
  const func = httpsCallable(functions, 'sendMeetingEmail');
  return (await func(data)).data;
};

/**
 * Envoie un rappel (1h ou 24h) pour une réunion spécifique
 */
export const sendMeetingReminder = async (data: {
  meetingId: string;
  title: string;
  date: string;
  time: string;
  participantEmails: string[];
  reminderType: "24h" | "1h";
}) => {
  const func = httpsCallable(functions, 'sendMeetingReminder');
  return (await func(data)).data;
};

/**
 * Envoie une notification générique par email (système, documents, nouveaux messages)
 */
export const sendNotificationEmail = async (data: any) => {
  const func = httpsCallable(functions, 'sendNotificationEmail');
  return (await func(data)).data;
};

export const sendPaymentEmail = async (data: any) => {
  const func = httpsCallable(functions, 'sendPaymentEmail');
  return (await func(data)).data;
};

export const sendPushNotification = async (data: any) => {
  const func = httpsCallable(functions, 'sendPushNotification');
  return (await func(data)).data;
};

export const sendSignatureCompleteNotification = async (data: any) => {
  const func = httpsCallable(functions, 'sendSignatureCompleteNotification');
  return (await func(data)).data;
};

/**
 * Notifie un destinataire qu'un document (NDA/LOI) attend sa signature
 */
export const sendSignatureNotification = async (data: {
  document_id: string;
  document_type: "nda" | "loi";
  recipient_email: string;
  recipient_name: string;
  deal_name: string;
  sender_name?: string;
}) => {
  const func = httpsCallable(functions, 'sendSignatureNotification');
  return (await func(data)).data;
};

/**
 * Envoie une alerte de sécurité ou de système (ex: changement de mot de passe)
 */
export const sendSystemAlertEmail = async (data: any) => {
  const func = httpsCallable(functions, 'sendSystemAlertEmail');
  return (await func(data)).data;
};